import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Film, Scissors, Send, ArrowRight } from 'lucide-react';
import AnimatedNumber from '@/components/premium/AnimatedNumber';
import { Link } from 'react-router-dom';
import { useT } from '@/lib/i18n';

/**
 * ROI Calculator — live inputs, instant outputs.
 *
 * Formula assumptions (documented so they can be tuned without magic numbers):
 *   HIGHLIGHTAI_MINUTES_PER_VIDEO = 4        // avg pipeline time
 *   CLIPS_PER_HOUR_SOURCE = 8                // avg clips extracted per hour
 *   MANUAL_MINUTES_PER_CLIP = 30             // manual edit time per clip in traditional workflow
 *
 * Outputs are meant to be *directional*, not marketing hyperbole.
 */

const HIGHLIGHTAI_MINUTES_PER_VIDEO = 4;
const CLIPS_PER_HOUR_SOURCE = 8;
const MANUAL_MINUTES_PER_CLIP = 30;

const Slider = ({ label, unit, value, onChange, min, max, step = 1, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-xs uppercase tracking-widest font-mono text-zinc-400">{label}</label>
      <span className="font-mono text-sm text-[#D4FF00] tabular-nums" data-testid={`${testid}-value`}>{value} {unit}</span>
    </div>
    <input
      type="range"
      min={min} max={max} step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="w-full accent-[#D4FF00]"
    />
    <div className="flex justify-between text-[10px] text-zinc-600 font-mono mt-1">
      <span>{min}</span><span>{max}</span>
    </div>
  </div>
);

const OutputCard = ({ icon: Icon, label, value, suffix = '', format = 'default', color = '#D4FF00', testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    className="p-5 rounded-xl border bg-white/[0.02]"
    style={{ borderColor: `${color}33` }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4" style={{ color }} />
      <span className="text-[10px] uppercase tracking-widest font-mono" style={{ color }}>{label}</span>
    </div>
    <AnimatedNumber value={value} format={format} suffix={suffix} className="font-heading text-3xl font-black" />
  </motion.div>
);

const ROICalculator = () => {
  const { lang } = useT();
  const [videosPerMonth, setVideosPerMonth] = useState(4);
  const [avgDurationMin, setAvgDurationMin] = useState(60);
  const [manualMinutesPerClip, setManualMinutesPerClip] = useState(MANUAL_MINUTES_PER_CLIP);

  const { clipsEstimated, hoursSavedPerMonth, manualHoursPerMonth, publishingFreq, contentIncrease } = useMemo(() => {
    const clips = Math.round(videosPerMonth * (avgDurationMin / 60) * CLIPS_PER_HOUR_SOURCE);
    const manualHours = Math.round((clips * manualMinutesPerClip) / 60);
    const highlightaiHours = Math.round((videosPerMonth * HIGHLIGHTAI_MINUTES_PER_VIDEO) / 60);
    const saved = Math.max(0, manualHours - highlightaiHours);
    const freq = clips ? Math.max(1, Math.round(clips / 30)) : 0;
    const increase = clips ? Math.round((clips / Math.max(videosPerMonth, 1)) * 100) : 0;
    return {
      clipsEstimated: clips,
      hoursSavedPerMonth: saved,
      manualHoursPerMonth: manualHours,
      publishingFreq: freq,
      contentIncrease: increase,
    };
  }, [videosPerMonth, avgDurationMin, manualMinutesPerClip]);

  return (
    <section id="roi" className="py-24 px-6 lg:px-8 dot-grid" data-testid="landing-roi">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5 font-mono">
            {lang === 'fr' ? 'Calculateur ROI' : 'ROI Calculator'}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
            {lang === 'fr' ? 'Combien de temps gagnes-tu par mois ?' : 'How many hours would you save per month?'}
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto text-sm sm:text-base">
            {lang === 'fr' ? 'Estimation basée sur des hypothèses transparentes — ajuste les curseurs.' : 'Estimate based on transparent assumptions — adjust the sliders.'}
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.15fr] gap-6">
          <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 space-y-6" data-testid="roi-inputs">
            <Slider
              label={lang === 'fr' ? 'Vidéos par mois' : 'Videos per month'}
              unit={lang === 'fr' ? 'vidéos' : 'videos'}
              value={videosPerMonth} onChange={setVideosPerMonth}
              min={1} max={30}
              testid="roi-input-videos"
            />
            <Slider
              label={lang === 'fr' ? 'Durée moyenne' : 'Average duration'}
              unit="min"
              value={avgDurationMin} onChange={setAvgDurationMin}
              min={10} max={180} step={5}
              testid="roi-input-duration"
            />
            <Slider
              label={lang === 'fr' ? 'Temps de montage manuel par clip' : 'Manual editing per clip'}
              unit="min"
              value={manualMinutesPerClip} onChange={setManualMinutesPerClip}
              min={10} max={90} step={5}
              testid="roi-input-editing"
            />
            <div className="pt-4 border-t border-white/5 text-[11px] font-mono text-zinc-500 leading-relaxed" data-testid="roi-assumptions">
              {lang === 'fr' ? (
                <>
                  <div><b>Hypothèses</b> :</div>
                  <div>· HighlightAI génère ~8 clips par heure de source</div>
                  <div>· Pipeline moyen : 4 min par vidéo</div>
                </>
              ) : (
                <>
                  <div><b>Assumptions</b>:</div>
                  <div>· HighlightAI extracts ~8 clips per hour of source</div>
                  <div>· Avg pipeline: 4 min per video</div>
                </>
              )}
            </div>
          </div>

          <div className="space-y-4" data-testid="roi-outputs">
            <div className="grid grid-cols-2 gap-4">
              <OutputCard icon={Clock} label={lang === 'fr' ? 'Heures gagnées / mois' : 'Hours saved / month'} value={hoursSavedPerMonth} suffix="h" testid="roi-output-hours" />
              <OutputCard icon={Scissors} label={lang === 'fr' ? 'Clips estimés' : 'Clips estimated'} value={clipsEstimated} testid="roi-output-clips" />
              <OutputCard icon={Send} label={lang === 'fr' ? 'Fréquence de publication' : 'Publishing cadence'} value={publishingFreq} suffix={lang === 'fr' ? ' / jour' : ' / day'} color="#00E5FF" testid="roi-output-freq" />
              <OutputCard icon={Film} label={lang === 'fr' ? 'Augmentation contenu' : 'Content increase'} value={contentIncrease} suffix="%" color="#A855F7" testid="roi-output-increase" />
            </div>
            <div className="p-5 rounded-xl border border-[#D4FF00]/25 bg-[#D4FF00]/[0.04]" data-testid="roi-summary">
              <div className="text-[10px] uppercase tracking-widest font-mono text-[#D4FF00] mb-1">
                {lang === 'fr' ? 'Résumé' : 'Summary'}
              </div>
              <p className="text-sm text-zinc-100 leading-relaxed">
                {lang === 'fr' ? (
                  <>Tu passerais de <span className="font-bold">{manualHoursPerMonth}h</span> de montage à <span className="font-bold text-[#D4FF00]">{Math.max(0, manualHoursPerMonth - hoursSavedPerMonth)}h</span> pour produire <span className="font-bold">{clipsEstimated}</span> clips.</>
                ) : (
                  <>You&apos;d move from <span className="font-bold">{manualHoursPerMonth}h</span> of editing to <span className="font-bold text-[#D4FF00]">{Math.max(0, manualHoursPerMonth - hoursSavedPerMonth)}h</span> to produce <span className="font-bold">{clipsEstimated}</span> clips.</>
                )}
              </p>
              <Link to="/register" className="inline-flex items-center gap-1.5 mt-3 text-sm text-[#D4FF00] hover:text-[#B3D600] font-semibold" data-testid="roi-cta">
                {lang === 'fr' ? 'Démarrer gratuitement' : 'Start free'} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
